import React, { useEffect, useRef, useState } from 'react';
import { 
  FaFutbol, FaBasketballBall, FaVolleyballBall, FaTableTennis,
  FaChess, FaRunning, FaSwimmer, FaMedal, FaTrophy,
  FaCalendarAlt, FaMapMarkerAlt, FaUsers, FaStar,
  FaArrowRight, FaCheckCircle, FaAward, FaHeartbeat,
  FaBaseballBall, FaHockeyPuck, FaBicycle, FaSkating
} from 'react-icons/fa';

const GameSportsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const sports = [
    { name: "Cricket", icon: <FaBaseballBall />, color: "from-green-500 to-green-600", players: 11, facilities: true },
    { name: "Football", icon: <FaFutbol />, color: "from-green-600 to-green-700", players: 11, facilities: true },
    { name: "Basketball", icon: <FaBasketballBall />, color: "from-orange-500 to-orange-600", players: 5, facilities: true },
    { name: "Volleyball", icon: <FaVolleyballBall />, color: "from-yellow-500 to-yellow-600", players: 6, facilities: true },
    { name: "Badminton", icon: <FaSkating />, color: "from-purple-500 to-purple-600", players: 2, facilities: true },
    { name: "Table Tennis", icon: <FaTableTennis />, color: "from-blue-500 to-blue-600", players: 2, facilities: true },
    { name: "Chess", icon: <FaChess />, color: "from-gray-500 to-gray-600", players: 2, facilities: true },
    { name: "Athletics", icon: <FaRunning />, color: "from-red-500 to-red-600", players: 1, facilities: true }
  ];

  const achievements = [
    { title: "University Level Champions", year: "2023", sport: "Cricket", achievement: "1st Place", icon: <FaTrophy /> },
    { title: "Inter-College Tournament", year: "2023", sport: "Basketball", achievement: "Runners Up", icon: <FaMedal /> },
    { title: "State Level Competition", year: "2022", sport: "Athletics", achievement: "Gold Medal", icon: <FaAward /> },
    { title: "District Tournament", year: "2023", sport: "Badminton", achievement: "Winner", icon: <FaTrophy /> }
  ];

  const upcomingEvents = [
    { name: "Annual Sports Meet", date: "15-20 April 2024", venue: "College Ground", participants: "All Students" },
    { name: "Inter-College Cricket Tournament", date: "5-10 May 2024", venue: "University Ground", participants: "College Team" },
    { name: "Badminton Championship", date: "25 May 2024", venue: "Indoor Sports Complex", participants: "All Students" }
  ];

  const facilities = [
    { name: "Cricket Ground", desc: "Full-sized cricket ground with pitch" },
    { name: "Basketball Court", desc: "International standard basketball court" },
    { name: "Volleyball Court", desc: "Indoor and outdoor volleyball courts" },
    { name: "Badminton Court", desc: "Indoor badminton courts with proper lighting" },
    { name: "Table Tennis Room", desc: "Multiple TT tables with equipment" },
    { name: "Chess Room", desc: "Dedicated room for chess tournaments" },
    { name: "Gymnasium", desc: "Modern gym with latest equipment" },
    { name: "Athletics Track", desc: "400m running track" }
  ];

  return (
    <section ref={sectionRef} className="w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 py-16">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          
          {/* Decorative Top */}
          <div className="flex justify-center gap-3 mb-6">
            <div className="w-12 h-1 bg-blue-500 rounded-full"></div>
            <div className="w-8 h-1 bg-blue-600 rounded-full"></div>
            <div className="w-6 h-1 bg-blue-700 rounded-full"></div>
          </div>

          {/* Heading */}
          <div className="text-center mb-10">
            <div className="flex justify-center mb-3">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-3 rounded-2xl shadow-lg">
                <FaFutbol className="text-white text-3xl" />
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              <span className="bg-gradient-to-r from-blue-700 to-indigo-800 bg-clip-text text-transparent">
                Games & Sports
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mx-auto"></div>
            <p className="text-gray-600 text-sm mt-4">
              Promoting physical fitness, teamwork, and sportsmanship
            </p>
          </div>

          {/* Sports Grid */}
          <div className={`grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-12 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            {sports.map((sport, idx) => (
              <div key={idx} className="bg-white rounded-xl p-4 text-center shadow-md hover:shadow-xl transition-all hover:-translate-y-1 group">
                <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${sport.color} flex items-center justify-center mx-auto mb-2 text-white text-xl group-hover:scale-110 transition-transform`}>
                  {sport.icon}
                </div>
                <p className="text-gray-800 font-semibold text-sm">{sport.name}</p>
                <p className="text-gray-400 text-xs">{sport.players} Players</p>
              </div>
            ))}
          </div>

          {/* Achievements Section */}
          <div className={`mb-12 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '100ms' }}>
            <div className="flex items-center gap-2 mb-6">
              <div className="w-1 h-6 bg-yellow-500 rounded-full"></div>
              <h3 className="text-xl font-bold text-gray-800">Our Achievements</h3>
            </div>
            <div className="grid md:grid-cols-4 gap-4">
              {achievements.map((item, idx) => (
                <div key={idx} className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 text-center border border-yellow-200 hover:shadow-lg transition-all">
                  <div className="text-yellow-500 text-3xl flex justify-center mb-2">{item.icon}</div>
                  <p className="text-gray-800 font-bold text-sm">{item.title}</p>
                  <p className="text-gray-600 text-xs">{item.sport} • {item.year}</p>
                  <p className="text-yellow-600 text-xs font-semibold mt-1">{item.achievement}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Facilities and Events Grid */}
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            
            {/* Sports Facilities */}
            <div className={`bg-white rounded-2xl shadow-xl p-6 transition-all duration-500 ${
              isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
            }`} style={{ transitionDelay: '200ms' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FaHeartbeat className="text-blue-600 text-xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Sports Facilities</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {facilities.map((facility, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <FaCheckCircle className="text-green-500 text-xs mt-0.5" />
                    <div>
                      <p className="text-gray-800 text-sm font-semibold">{facility.name}</p>
                      <p className="text-gray-400 text-xs">{facility.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Upcoming Events */}
            <div className={`bg-white rounded-2xl shadow-xl p-6 transition-all duration-500 ${
              isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
            }`} style={{ transitionDelay: '300ms' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FaCalendarAlt className="text-blue-600 text-xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Upcoming Events</h3>
              </div>
              <div className="space-y-4">
                {upcomingEvents.map((event, idx) => (
                  <div key={idx} className="border-b border-gray-100 pb-3 last:border-0">
                    <p className="text-gray-800 font-semibold text-sm">{event.name}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <div className="flex items-center gap-1">
                        <FaCalendarAlt className="text-blue-400 text-xs" />
                        <span className="text-gray-500 text-xs">{event.date}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FaMapMarkerAlt className="text-blue-400 text-xs" />
                        <span className="text-gray-500 text-xs">{event.venue}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <FaUsers className="text-blue-400 text-xs" />
                      <span className="text-gray-400 text-xs">{event.participants}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Coach Section */}
          <div className={`bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 mb-10 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '400ms' }}>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                  <FaUsers className="text-white text-2xl" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-xl">Professional Coaching Staff</h3>
                  <p className="text-blue-100 text-sm">Experienced coaches for all sports</p>
                </div>
              </div>
              <button className="bg-white text-blue-600 px-5 py-2 rounded-lg font-semibold text-sm hover:shadow-lg transition-all flex items-center gap-2">
                Meet Our Coaches
                <FaArrowRight className="text-sm" />
              </button>
            </div>
          </div>

          {/* Join Sports Club */}
          <div className={`text-center transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '500ms' }}>
            <p className="text-gray-600 text-sm mb-3">Interested in representing our college?</p>
            <button className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl font-semibold text-sm hover:shadow-2xl transition-all hover:scale-105">
              Join Sports Club
            </button>
          </div>

          {/* Bottom Decorative */}
          <div className="mt-10 flex justify-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default GameSportsSection;